import { getJitsiConfig, getJitsiInterfaceConfig } from 'features/Rooms/RoomView/config/jitsi';
import { IResource } from 'kits/apiKit3/legacy';
import { useEvent } from 'kits/eventKit';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { aHandRaiseButtonDisabled } from 'shared-state/directory/atoms';
import { useIsOverlayModalOpen } from 'shared-state/display-options/hooks';
import { useSelectedAttachment } from 'shared-state/location/hook';
import { useStatePersist } from 'use-state-persist';
import { hardcodedAddOns } from '../addons';
import { ICallAction, ICallActionKey, useCallActions } from '../CallActions';
import { useCallState } from './callstate';
import { aIsBackgroundDialogOpen } from '../atom';
import { IJitsiMeetExternalApi } from '@jitsi/react-sdk/lib/types/';
import { useIdentityClaims } from 'shared-state/identity/hooks';
import { aPreferredLanguage } from 'features/LinkAttachmentModalBox/atom';
import { createDragHandlers } from './components/dragHandlers';
import { IRoomCallControllerStateProps, IRoomCallEventListener } from './types';

const useRoomCallController: (
    actions: Array<Array<ICallActionKey>>,
    profileImageURL: string | undefined,
    callEventListeners: Array<IRoomCallEventListener> | undefined,
    muteOnJoin: 'audio' | 'both' | 'video' | undefined,
    room: IResource | undefined,
    onAPI: ((api: any) => void) | undefined,
    roomName: string,
    onAddOnDataRecieved?: ((addonId: string, data: any) => void) | undefined,
    userIsAloneInOwnPersonalRoom?: boolean
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
) => IRoomCallControllerStateProps = (actions, profileImageURL, callEventListeners, muteOnJoin, room, onAPI, roomName, userIsAloneInOwnPersonalRoom) => {
    const [jitsiAPI, setJitsiAPI] = useState<any>();
    const [checkForInstanceToggling, setCheckForInstanceToggling] = useState(false);

    const [isBackgroundDialogOpen, setIsBackgroundDialogOpen] = useRecoilState<any>(aIsBackgroundDialogOpen);

    const [identityClaims] = useIdentityClaims();

    const userEmailAddress = identityClaims && identityClaims.upn ? identityClaims.upn : 'unknown.email@ivicos-campus.app';

    const preferredLanguage = useRecoilValue(aPreferredLanguage);

    useEffect(() => {
        if (selectedAttachment) setSelectedAttachment(undefined); // Reset the selected attachment after processing)
    }, [roomName]);

    useEffect(() => {
        if (jitsiAPI !== undefined) {
            jitsiAPI.executeCommand('setUILanguage', preferredLanguage);

            if (isBackgroundDialogOpen) {
                window.location.href = '#';
                jitsiAPI.executeCommand('toggleVirtualBackgroundDialog');
            }
        }
        setIsBackgroundDialogOpen(false);
    }, [isBackgroundDialogOpen, jitsiAPI, preferredLanguage]);

    const jitsiConfig = getJitsiConfig(muteOnJoin);

    // const interfaceConfig: InterfaceConfigOptions = getJitsiInterfaceConfig(roomName.includes('personal-'));
    const interfaceConfig = getJitsiInterfaceConfig();

    const [overlayURI] = useStatePersist<string | undefined>('@exp:overlay');
    const [overlayIsTransparent] = useStatePersist<boolean>('@exp:overlay:istransparent');

    const updateOverlay = () => {
        typeof overlayURI != 'undefined' && typeof overlayIsTransparent != 'undefined' && setOverlay(overlayURI || '', overlayIsTransparent);
    };

    const setOverlay = (url: string, isTransparent: boolean) => {
        jitsiAPI.setForegroundOverlay(url || '', '', isTransparent ? 'fusion' : 'circle');
    };
    const overlayListener = {
        eventName: 'videoMuteStatusChanged',
        handler: (e: any) => {
            if (!e.muted) updateOverlay();
        }
    };

    const [selectedAttachment, setSelectedAttachment] = useSelectedAttachment();
    const addOnIsVisible = selectedAttachment ? true : false;
    // CallState
    const {
        eventListeners,
        activeAddOn,
        cameraIsMuted,
        micIsMuted,
        sharingIsOn,
        isConnected,
        tileViewIsEnabled,
        encryptionIsEnabled,
        stats,
        statsAreVisible,
        executeCommand,
        showDeviceSettings,
        isLocalUserHandRaised,
        isChatVisible
    } = useCallState(roomName, room, jitsiAPI, [...(callEventListeners || []), ...[overlayListener]]);

    const shouldDisableButton = cameraIsMuted && micIsMuted && userIsAloneInOwnPersonalRoom && !sharingIsOn;

    const shouldShowFireFoxAlert =
        !(window.location.pathname.includes('/w/') || window.location.pathname.includes('/visitorLandingPage/')) &&
        navigator.userAgent.search('Firefox') > -1 &&
        !userIsAloneInOwnPersonalRoom;

    // Actions
    const callActions = useCallActions({
        tileViewIsEnabled,
        cameraIsMuted,
        sharingIsOn,
        micIsMuted,
        encryptionIsEnabled,
        statsAreVisible,
        addOnIsVisible,
        activeAddOn,
        shouldDisableButton,
        isLocalUserHandRaised,
        isChatVisible
    });

    const actionBarActions = actions.map((actionGroup, actionGroupIndex) => {
        const groupActions: Array<ICallAction> = actionGroup.map((action) => {
            return callActions[action];
        });

        return {
            key: 'action-group-' + actionGroupIndex,
            actions: groupActions.filter((a) => a != undefined)
        };
    });

    useEffect(() => {
        const api = jitsiAPI;

        // eslint-disable-next-line @typescript-eslint/no-empty-function
        if (!api) return () => {};

        onAPI && onAPI(api);

        updateOverlay();

        eventListeners.forEach((listener: any) => {
            api.addEventListener(listener.eventName, listener.handler);
        });

        api.addEventListener('notificationTriggered', ({ title }) => {
            if (title === 'notify.mutedRemotelyTitle') {
                //! Toggle audio twice to fix the issue where the API reports the audio as muted when it is not
                executeCommand('toggleAudio');
                executeCommand('toggleAudio');
            }
        });

        setTimeout(() => {
            setCheckForInstanceToggling(true);
        }, 3500);

        return () => {
            eventListeners.forEach((listener: any) => {
                api.removeEventListener(listener.eventName, listener.handler);
            });
            api.removeEventListener('notificationTriggered');
        };
    }, [jitsiAPI, overlayURI, overlayIsTransparent]);

    useEffect(() => {
        if (!jitsiAPI || !checkForInstanceToggling) return;

        jitsiAPI
            .isAudioMuted()
            .then((muted) => {
                if (muted !== micIsMuted) {
                    //! Toggle audio twice to fix the issue where the API reports the audio as muted when it is not
                    executeCommand('toggleAudio');
                    executeCommand('toggleAudio');
                }
            })
            .catch((error) => {
                console.error('Error checking audio mute status:', error);
            });

        setTimeout(() => {
            setCheckForInstanceToggling(false);
        }, 1000);
    }, [jitsiAPI, checkForInstanceToggling]);

    const [, setHandRaiseButtonDisabled] = useRecoilState<boolean>(aHandRaiseButtonDisabled);

    const handleJitsiMeetExternalAPILoad = (api: IJitsiMeetExternalApi) => {
        console.log('API loaded (JitsiMeetExternalApi)');
        api.executeCommand('subject', ' ');
        setJitsiAPI(api);
        setHandRaiseButtonDisabled(false);
    };

    const [isVideoHidden, setIsVideoHidden] = useState(false);

    // The floating call tile is a live-video iframe (see JitsiWrapper.tsx), which browsers can
    // promote to its own hardware compositing layer that paints above regular DOM content
    // regardless of z-index - a widget modal opening on top of it can't reliably out-z-index it.
    // Sliding it off-screen via the existing isVideoHidden mechanism (an actual position change,
    // not stacking order) sidesteps that entirely. Remembers the user's own hidden/visible
    // preference from just before the modal opened and restores it once the modal closes, rather
    // than always forcing the tile back to visible.
    const isOverlayModalOpen = useIsOverlayModalOpen();
    const videoHiddenBeforeOverlayModal = React.useRef(false);
    useEffect(() => {
        if (isOverlayModalOpen) {
            videoHiddenBeforeOverlayModal.current = isVideoHidden;
            setIsVideoHidden(true);
        } else {
            setIsVideoHidden(videoHiddenBeforeOverlayModal.current);
        }
        // Deliberately keyed on isOverlayModalOpen alone - including isVideoHidden here would fire
        // this on every manual toggle too and immediately overwrite the user's own click.
    }, [isOverlayModalOpen]);

    const [isDragging, setIsDragging] = useState(false);
    const [movementDirection, setMovementDirection] = useState({ x: 0, y: 0 });
    const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 });

    const translateToHideOrShow = React.useCallback(() => {
        return isVideoHidden ? { x: -409, y: 0 } : movementDirection;
    }, [isVideoHidden, movementDirection]);

    const [jitsiShowHideInnerContainerOpacity, setJitsiShowHideInnerContainerOpacity] = useState(0);

    const activeAddOnData = hardcodedAddOns.find((a) => a.id == selectedAttachment?.addOnId);

    useEvent('disable-camera', () => {
        cameraIsMuted == false ? executeCommand('toggleVideo') : null;
    });

    useEvent('disable-mic', () => {
        if (micIsMuted == false && muteOnJoin == 'audio') {
            return null;
        }
        micIsMuted == false ? executeCommand('toggleAudio') : null;
    });

    const extractPositionFromEvent = React.useCallback((event: React.MouseEvent) => {
        const positionX = event.screenX;
        const positionY = event.screenY;
        return { x: positionX, y: positionY };
    }, []);

    useEffect(() => {
        let timer;
        if (!selectedAttachment) setJitsiShowHideInnerContainerOpacity(0);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        else timer = setTimeout(() => setJitsiShowHideInnerContainerOpacity(1), 500);
    }, [selectedAttachment]);

    const startingPosition = { bottom: 48, left: 48 };
    const { onDragStartHandler, onDragEndHandler } = createDragHandlers({
        extractPositionFromEvent,
        setIsDragging,
        setDragStartPosition,
        isDragging,
        dragStartPosition,
        movementDirection,
        setMovementDirection,
        startingPosition
    });

    return {
        actionBarActions,
        activeAddOn,
        activeAddOnData,
        addOnIsVisible,
        cameraIsMuted,
        dragStartPosition,
        encryptionIsEnabled,
        eventListeners,
        executeCommand,
        extractPositionFromEvent,
        handleJitsiMeetExternalAPILoad,
        interfaceConfig,
        isChatVisible,
        isConnected,
        isDragging,
        isLocalUserHandRaised,
        isVideoHidden,
        jitsiAPI,
        jitsiConfig,
        jitsiShowHideInnerContainerOpacity,
        micIsMuted,
        movementDirection,
        profileImageURL,
        selectedAttachment,
        setDragStartPosition,
        setIsDragging,
        setIsVideoHidden,
        setMovementDirection,
        setSelectedAttachment,
        sharingIsOn,
        showDeviceSettings,
        stats,
        statsAreVisible,
        tileViewIsEnabled,
        translateToHideOrShow,
        userEmailAddress,
        shouldShowFireFoxAlert,
        startingPosition,
        onDragStartHandler,
        onDragEndHandler
    };
};

export default useRoomCallController;
